import { useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Container, Box, Typography } from '@mui/material';
import { CheckCircle, NavigateNext } from '@mui/icons-material';
import dayjs from 'dayjs';
import { Breadcrumbs } from '../../../../components/shared';
import { routeConstants } from '../../../../constants/routeConstants';
import { MESSAGE } from '../../../../constants/message';
import ConfirmationModal from '../../../../components/ConfirmationModel/ConfirmationModal';
import TimerModal from '../../../../components/TimerModal/TimerModal';
import { FeedSourceDataPanel, HeaderInfoCard, ActionButtons, LoadingModal, WarningPanel } from './index';
import { useGLPostingDetails } from '../../hooks/useGLPostingDetails';
import {
  ACTION_LABELS,
  ACTION_VALUES,
  DATE_CONSTRAINTS,
  ICON_SIZES,
  LABELS,
  MODAL_CONFIG,
  ROUTES,
  UI_TEXT,
} from '../../constants';
import {
  defaultApprovedFilterState,
  setApprovedGLPostingFilters,
} from '../../../../redux-slice/weeklyGLPostingFilters';
import useStyles from './style';

const displayName = 'GLPostingDetails';

const GLPostingDetails = ({ isEmbedded = false }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { headerData, breadcrumbData, filters, sublocationState, postingState, dataPanel, modals } =
    useGLPostingDetails({ isEmbedded });

  const {
    productTypes,
    selectedProductType,
    currencies,
    selectedCurrencies,
    selectedX3ProductName,
    onProductTypeChange,
    onCurrencyChange,
    isProductTypesLoading,
    isCurrenciesLoading,
    onStartDateChange,
    onEndDateChange,
    isStartDateDisabled,
    isEndDateDisabled,
    minStartDate,
    maxStartDate,
    minEndDate,
    maxEndDate,
    shouldDisableStartDate,
    shouldDisableEndDate,
    dateConstraintHint,
    dateConstraintWarnings = [],
    selectedStartDate,
    selectedEndDate,
    selectedPostingDate,
    onPostingDateChange,
  } = filters;

  const {
    sublocations,
    allSublocations,
    selectedSublocations,
    searchTerm: sublocationSearchTerm,
    isLoading: isLoadingSublocations,
    onChange: onSublocationChange,
    onSearch: onSublocationSearch,
    onSelectAll: onSelectAllSublocations,
    onDeselectAll: onDeselectAllSublocations,
    onApply: onApplySublocations,
  } = sublocationState;

  const { isApprovedPosting, approvedPostingData, isApproved, isSubmitting, isApproveDisabled, onApproveClick } =
    postingState;

  const { warnings, feedSourceData, sublocationBreakdown, isApplyingFilters, isFetchingFeeds, resyncData } = dataPanel;

  const combinedWarnings = useMemo(() => {
    const gapWarnings = Array.isArray(dateConstraintWarnings) ? dateConstraintWarnings.filter(Boolean) : [];
    const apiWarnings = Array.isArray(warnings) ? warnings.filter(Boolean) : [];
    return [...gapWarnings, ...apiWarnings];
  }, [dateConstraintWarnings, warnings]);

  const {
    showApproveModal,
    showSuccessModal,
    showFailureModal,
    failureMessage,
    showApiErrorModal,
    apiErrorMessage,
    createdPosting,
    onApproveConfirm,
    onFailureAction,
    onCloseSuccess,
    onCloseApiErrorModal,
  } = modals;

  const isSuccessTransitionInEmbedded = isEmbedded && showSuccessModal;
  const visualApprovedState = isSuccessTransitionInEmbedded ? false : isApproved;

  const handleSuccessModalComplete = useCallback(() => {
    onCloseSuccess?.();

    if (!createdPosting?.id || !isApproved || isApprovedPosting) return;

    if (isEmbedded) {
      dispatch(setApprovedGLPostingFilters(defaultApprovedFilterState));
      navigate(`/${routeConstants.WEEKLY_GL_POSTING_ROUTE}${ROUTES.QUERY_STRING_APPROVED}`);
      return;
    }

    navigate(
      `/${routeConstants.WEEKLY_GL_POSTING_ROUTE}/${routeConstants.WEEKLY_GL_POSTING_APPROVED}/${createdPosting.id}`,
      { state: { isApproved: true } },
    );
  }, [onCloseSuccess, createdPosting?.id, isApproved, isApprovedPosting, isEmbedded, dispatch, navigate]);

  const safeCustomerName = useMemo(() => headerData?.customerName ?? LABELS.NOT_AVAILABLE, [headerData?.customerName]);
  const pageHeaderText = useMemo(
    () => (isApprovedPosting ? approvedPostingData?.postingId ?? LABELS.NOT_AVAILABLE : safeCustomerName),
    [isApprovedPosting, approvedPostingData?.postingId, safeCustomerName],
  );
  const safeWeekLabel = useMemo(() => {
    if (!selectedStartDate || !selectedEndDate) return LABELS.NOT_AVAILABLE;
    const start = dayjs(selectedStartDate);
    const end = dayjs(selectedEndDate);
    if (!start.isValid() || !end.isValid()) return LABELS.NOT_AVAILABLE;
    return `${start.format(DATE_CONSTRAINTS.DISPLAY_DATE_FORMAT)} - ${end.format(
      DATE_CONSTRAINTS.DISPLAY_DATE_FORMAT,
    )}`;
  }, [selectedStartDate, selectedEndDate]);

  const successPostingLabel = useMemo(
    () => selectedX3ProductName || safeCustomerName,
    [selectedX3ProductName, safeCustomerName],
  );

  const filterPropsForHeader = {
    productTypes,
    selectedProductType,
    currencies,
    selectedCurrencies,
    onProductTypeChange,
    onCurrencyChange,
    isProductTypesLoading,
    isCurrenciesLoading,
  };

  const datePropsForHeader = {
    selectedStartDate,
    selectedEndDate,
    onStartDateChange,
    onEndDateChange,
    isStartDateDisabled,
    isEndDateDisabled,
    minStartDate,
    maxStartDate,
    minEndDate,
    maxEndDate,
    shouldDisableStartDate,
    shouldDisableEndDate,
    dateConstraintHint,
    dateConstraintWarnings,
    selectedPostingDate,
    onPostingDateChange,
  };

  const sublocationPropsForHeader = {
    sublocations,
    allSublocations,
    selectedSublocations,
    onSublocationChange,
    onSublocationSearch,
    sublocationSearchTerm,
    onSelectAllSublocations,
    onDeselectAllSublocations,
    onApplySublocations,
    isLoadingSublocations,
  };

  return (
    <Container maxWidth="xl" disableGutters className={classes.root}>
      {!isEmbedded && (
        <Box className={classes.breadcrumbContainer}>
          <Breadcrumbs
            icon={<NavigateNext fontSize="medium" />}
            variant="body2"
            underline="hover"
            data={breadcrumbData}
            data-testid="breadcrumb"
          />
        </Box>
      )}

      {!isEmbedded && (
        <Box className={classes.pageTitle}>
          <Typography variant="h1" data-testid="customerName">
            {pageHeaderText}
          </Typography>
        </Box>
      )}

      <HeaderInfoCard
        filterProps={filterPropsForHeader}
        dateProps={datePropsForHeader}
        sublocationProps={sublocationPropsForHeader}
        isReadOnly={isApprovedPosting || visualApprovedState}
        approvedSummary={approvedPostingData}
        isApplyingFeeds={isFetchingFeeds}
        resyncData={resyncData}
      />

      <FeedSourceDataPanel
        data={feedSourceData}
        sublocationBreakdown={sublocationBreakdown}
        isApplied={isApplyingFilters}
        isApproved={visualApprovedState}
        isLoading={isFetchingFeeds}
      />

      {combinedWarnings.length > 0 && (isApplyingFilters || !visualApprovedState) && (
        <WarningPanel warnings={combinedWarnings} warningCount={combinedWarnings.length} />
      )}

      <ActionButtons
        isApproved={visualApprovedState}
        canAddNewRow={false}
        isApproveDisabled={isApproveDisabled}
        showApproveButton={isApplyingFilters}
        selectedProductType={selectedProductType}
        selectedCurrencyCount={selectedCurrencies.length}
        selectedSublocationCount={selectedSublocations.length}
        hasDateConstraintIssue={Boolean(dateConstraintHint) || dateConstraintWarnings.length > 0}
        selectedStartDate={selectedStartDate}
        selectedEndDate={selectedEndDate}
        selectedPostingDate={selectedPostingDate}
        onAddNewRow={() => {}}
        onApproveClick={onApproveClick}
      />

      <LoadingModal isOpen={isSubmitting} />

      <ConfirmationModal
        isOpen={showApproveModal}
        title={MESSAGE.CONFIRMATION}
        msg={MESSAGE.GL_POSTING_APPROVE_CONFIRMATION(safeWeekLabel)}
        buttons={[
          { text: MESSAGE.APPROVE, value: MESSAGE.APPROVE },
          { text: MESSAGE.CANCEL, value: MESSAGE.CANCEL },
        ]}
        onClick={onApproveConfirm}
        isLoading={isSubmitting}
      />

      <TimerModal
        isOpen={showSuccessModal}
        timer={MODAL_CONFIG.SUCCESS_TIMER_SECONDS}
        activate={showSuccessModal}
        onTimeComplete={handleSuccessModalComplete}
        onClose={handleSuccessModalComplete}
        allowBackdropClick
        icon={<CheckCircle color="success" sx={{ fontSize: ICON_SIZES.SUCCESS }} />}
        title={MESSAGE.SUCCESSFUL}
        msg={
          <>
            {UI_TEXT.POSTING_SUCCESS_PREFIX} <strong>{successPostingLabel}</strong> {UI_TEXT.POSTING_SUCCESS_MIDDLE}{' '}
            <strong>{safeWeekLabel}</strong> {UI_TEXT.POSTING_SUCCESS_SUFFIX} {UI_TEXT.POSTING_SUCCESS_TIMER}
          </>
        }
      />

      <ConfirmationModal
        isOpen={showFailureModal}
        title={MESSAGE.FAILED}
        msg={failureMessage}
        buttons={[
          { text: ACTION_LABELS.TRY_AGAIN, value: ACTION_VALUES.TRY_AGAIN },
          { text: MESSAGE.CANCEL, value: MESSAGE.CANCEL },
        ]}
        onClick={onFailureAction}
      />

      <ConfirmationModal
        isOpen={showApiErrorModal}
        title={MESSAGE.FAILED}
        msg={apiErrorMessage}
        buttons={[{ text: MESSAGE.OK, value: MESSAGE.OK }]}
        onClick={onCloseApiErrorModal}
      />
    </Container>
  );
};

GLPostingDetails.displayName = displayName;

export default GLPostingDetails;
